<template>

    <div id="place-order">

        <div class="lg:flex items-center mb-4">
            <h1 class="flex-1 text-2xl font-medium text-gray-900 capitalize mb-2 lg:mb-0">Place Order</h1>

            <div class="hidden lg:flex w-2/5 xl:hidden">
                <div class="flex items-center text-sm" v-if="reference_changed === false">
                    Why not change your order reference to something more meaningful?
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" focusable="false" role="img" aria-label="Change Order Reference">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                </div>
            </div>

            <div class="form-control flex items-center" v-if="loaded">
                <div for="order_reference" class="hidden xl:flex xl:w-48 text-sm font-medium text-right xl:mr-2">Order Reference</div>
                <input type="text" name="order_reference" id="order_reference" class="masked-form-input" placeholder="Order Reference" autocomplete="off" v-model="form.reference" aria-label="Order Reference" aria-required="true" @change="markReferenceChanged">
            </div>

            <div class="hidden xl:flex xl:w-1/5 xl:ml-4">
                <div class="flex items-center text-sm" v-if="reference_changed === false">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" focusable="false" role="img" aria-label="Change Order Reference">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Why not change your order reference to something more meaningful?
                </div>
            </div>
        </div>

        <div v-if="loaded">

            <div class="flex mb-4" v-if="form.fieldHasErrors('reference')">
                <div class="flex-1">
                    <div class="flex border-l-4 border-error-500 bg-error-50 px-4 py-1 rounded tracking-wide shadow items-center text-error-700">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" focusable="false" role="img" aria-label="An Error Occurred">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        <p class="text-error-700 font-medium text-sm">
                            {{ form.getFieldError('reference') }}
                        </p>
                    </div>
                </div>
                <div class="hidden xl:flex xl:w-1/5 xl:ml-4">
                    &nbsp;
                </div>
            </div>

            <div class="flex mb-4" v-else-if="form.hasError()">
                <div class="flex-1">
                    <div class="flex border-l-4 border-error-500 bg-error-50 px-4 py-1 rounded tracking-wide shadow items-center text-error-700">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" focusable="false" role="img" aria-label="An Error Occurred">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        <p class="text-error-700 font-medium text-sm">
                            {{ form.getErrorMessage() }}
                        </p>
                    </div>
                </div>

                <div class="hidden xl:flex xl:w-1/5 xl:ml-4">
                    &nbsp;
                </div>
            </div>

            <div class="xl:flex">
                <div class="flex-1 bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6 mb-6">

                    <div class="pb-4">
                        <div class="flex items-center">
                            <h2 class="text-lg font-medium leading-6 text-gray-900 flex-1">Your Order</h2>
                        </div>

                        <p class="mt-1 text-sm text-gray-500">
                            Place your order then download your product codes.
                        </p>
                    </div>

                    <div class="pb-4">
                        <!-- Order Item Titles -->
                        <div class="grid grid-cols-2 lg:grid-cols-6 xl:grid-cols-7 gap-6 uppercase text-center border-b-2 border-gray-100 p-2">
                            <div class="hidden lg:flex justify-center form-control col-span-2">
                                <span class="block text-sm font-medium text-gray-700">Product</span>
                            </div>
                            <div class="hidden xl:flex justify-center form-control">
                                <span class="block text-sm font-medium text-gray-700">Type</span>
                            </div>
                            <div class="form-control">
                                <span class="block text-sm font-medium text-gray-700">Value</span>
                            </div>
                            <div class="form-control">
                                <span class="block text-sm font-medium text-gray-700">Quantity</span>
                            </div>
                            <div class="hidden xl:flex form-control">
                                &nbsp;
                            </div>
                        </div>

                        <!-- Order Items -->
                        <div id="order-items" v-for="(item, index) in form.data">
                            <OrderItem v-model="form" :products="products" :item="item" :index="index"></OrderItem>
                        </div>
                        <div id="no-order-items" class="py-8 border-b border-gray-200 text-center" v-if="form.data.length === 0">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 mx-auto text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" focusable="false" role="img" aria-label="Add items to your order">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                            </svg>
                            <h3 class="text-lg font-medium leading-6 text-gray-900">
                                Add items to your order
                            </h3>
                            <p class="mt-1 text-sm text-gray-500 text-center">
                                Use the form below to select a product to add to your order.
                            </p>
                        </div>

                        <!-- Add Item Form -->
                        <OrderAddItemForm v-model="form" :products="products"></OrderAddItemForm>

                    </div>
                </div>

                <div class="w-full xl:w-1/5 xl:ml-4">
                    <div class="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6 mb-6">
                        <h2 class="font-medium leading-6 text-gray-900 text-lg">Order Summary</h2>

                        <div class="pt-4" v-if="form.data.length">
                            <table class="w-full">
                                <tbody>
                                    <tr>
                                        <td class="text-left text-sm text-gray-600">Quantity</td>
                                        <td class="text-right text-gray-700">{{ orderQuantity }}</td>
                                    </tr>
                                    <tr>
                                        <td class="text-left text-sm text-gray-600">Gross Total</td>
                                        <td class="text-right text-gray-700">{{ formatCurrency(form.order_currency, grossTotal) }}</td>
                                    </tr>
                                    <tr>
                                        <td class="text-left text-sm text-gray-600">Tax</td>
                                        <td class="text-right text-gray-700">{{ formatCurrency(form.order_currency, tax) }}</td>
                                    </tr>
                                    <tr>
                                        <td class="text-left text-sm text-gray-600">Discount</td>
                                        <td class="text-right text-gray-700 font-medium text-brand-700">{{ formatCurrency(form.order_currency, discount) }}</td>
                                    </tr>
                                </tbody>
                            </table>

                            <hr class="my-2">

                            <table class="w-full">
                                <tbody>
                                    <tr>
                                        <td class="text-left text-sm font-medium text-gray-800">Total</td>
                                        <td class="text-right text-xl font-medium text-gray-900">{{ formatCurrency(form.order_currency, netTotal) }}</td>
                                    </tr>
                                </tbody>
                            </table>

                            <hr class="my-2">

                            <p class="text-xs text-gray-600">
                                Please Note: Additional transaction fees may be applied to your order, depending on your
                                purchasing agreements.
                            </p>

                        </div>
                        <div class="py-6 text-center" v-else>
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 mx-auto text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" focusable="false" role="img" aria-label="No order summary">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>

                            <h3 class="font-medium leading-6 text-gray-900">
                                No Products Selected
                            </h3>

                            <p class="mt-1 text-xs text-gray-500 text-center">
                                You have not selected any products, add items to your order.
                            </p>
                        </div>

                    </div>

                    <div class="flex mb-4" v-if="$parent.setupIncompleteBanner">
                        <p class="text-sm">
                            Please update your <router-link :to="{ name: 'settings' }">account settings</router-link>
                            before placing an order.
                        </p>
                    </div>

                    <div class="flex mt-2 justify-end">
                        <button class="btn-error justify-center px-6 w-full" :disabled="$parent.setupIncompleteBanner || !orderHasItems || form.isFormUpdating()" @click.prevent="placeOrder">
                            <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" v-if="form.isFormUpdating()">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Place Order
                        </button>
                    </div>
                </div>
            </div>



        </div>
        <Loading message="Loading" v-else></Loading>

    </div>

</template>

<script>
import Loading from "../Support/Loading";
import {Order} from "../../app/Order/Order";
import OrderAddItemForm from "./OrderAddItemForm";
import OrderItem from "./OrderItem";
import {Product} from "../../app/Product/Product";
import {FormattingMixin, PermissionMixin} from "../../support/mixins";

export default {
    name: "PlaceOrder",
    mixins: [PermissionMixin, FormattingMixin],
    data() {
        return {
            reference_changed: false,
            products: null,
            form: null,
            loaded: false
        }
    },
    components: {
        OrderAddItemForm,
        OrderItem,
        Loading
    },
    computed: {
        orderHasItems() {
            return this.form.data.length > 0;
        },
        orderQuantity() {
            let total = 0;

            if (this.orderHasItems) {
                for (let i = 0; i < this.form.data.length; i++) {
                    let item = this.form.data[i];

                    total += Number.parseInt(item.quantity);
                }
            }

            return total;
        },
        grossTotal() {
            let total = 0;

            if (this.orderHasItems) {
                for (let i = 0; i < this.form.data.length; i++) {
                    let item = this.form.data[i];

                    total += (item.quantity * item.value);
                }
            }

            return total;
        },
        tax() {
            let total = 0;

            if (this.orderHasItems) {
                for (let i = 0; i < this.form.data.length; i++) {
                    let item = this.form.data[i];
                    let product = this.products.products.data.find(product => product.sku === item.sku);

                    let itemTotal = (item.quantity * item.value);

                    total += (itemTotal - (itemTotal / (1 + (product.tax / 10000))));
                }
            }

            return total;
        },
        discount() {
            let total = 0;

            if (this.orderHasItems) {
                for (let i = 0; i < this.form.data.length; i++) {
                    let item = this.form.data[i];
                    let product = this.products.products.data.find(product => product.sku === item.sku);

                    let itemTotal = (item.quantity * item.value);

                    total += (itemTotal * (product.discount / 10000));
                }
            }

            return total;
        },
        netTotal() {
            return this.grossTotal - this.discount;
        }
    },
    methods: {
        placeOrder() {
            let vm = this;

            vm.form.createOrder().then(response => {
                vm.$router.push({ name: 'order.show',  params: { reference: vm.form.reference }});
            }).catch(error => {
                this.form.reference = this.form.generateOrderReference('SR-', vm.$parent.auth);
                vm.handleError(error)
            });
        },
        markReferenceChanged() {
            this.reference_changed = true;
        }
    },
    mounted() {
        let vm = this;

        new Promise(resolve => {
            vm.form = new Order(vm.$parent.auth);
            vm.products = new Product();

            vm.products.all(vm.form.order_currency).then(r => {
                resolve(true);
            }).catch(error => vm.handleError(error));
        }).then(result => {
            vm.loaded = result;
        });
    }
}
</script>


